import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { AuthWrapper } from 'app/Wrappers/AuthWrapper';
import { ArrowBack as ArrowBackIcon } from '@material-ui/icons';
import DialogNotifyWindow from 'app/components/DialogNotifyWindow/DialogNotifyWindow';
import styles from './SendVerification.module.scss';
import { actions } from '../../../store/ducks/auth/actions';
import config from 'config';
import { 
  executeRecaptcha, 
  createOnRecaptchaExpired, 
  createOnRecaptchaErrored, 
  handleRecaptchaError 
} from '../../../utils/recaptchaUtils';

const SendVerification = () => {
  const verifyEmail = localStorage.getItem('verifyEmail');
  const dispatch = useDispatch();
  const recaptchaRef = useRef();
  const mountedRef = useRef(true);
  const executingCaptchaRef = useRef(false);

  // Add selector for error state
  const errorset = useSelector(state => state.auth.forgotPassError);

  const [dialogMessage, setDialogMessage] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [isResending, setIsResending] = useState(false); // Add loading state
  const [isExecutingCaptcha, setIsExecutingCaptcha] = useState(false);

  // Clear errors on component mount
  useEffect(() => {
    dispatch(actions.forgotPassError(''));
    return () => {
      mountedRef.current = false;
      dispatch(actions.forgotPassError(''));
    };
  }, [dispatch]);

  // Watch for errors and show them in dialog
  useEffect(() => {
    if (errorset) {
      setDialogMessage(errorset);
      setShowDialog(true);
    }
  }, [errorset]);

  // Create reCAPTCHA event handlers using shared utility
  const onRecaptchaExpired = createOnRecaptchaExpired({
    recaptchaRef,
    dispatch,
    errorAction: actions.forgotPassError
  });

  const onRecaptchaErrored = createOnRecaptchaErrored({
    recaptchaRef,
    dispatch,
    errorAction: actions.forgotPassError
  });

  const handleResend = async () => {
    try {
      setIsResending(true);
      let recaptchaToken = null;

      // Get reCAPTCHA token if enabled
      if (config.recaptcha.ENABLE_GOOGLE_RECAPTCHA) {
        try {
          recaptchaToken = await executeRecaptcha({
            recaptchaRef,
            mountedRef,
            executingCaptchaRef,
            setIsExecutingCaptcha,
            dispatch,
            errorAction: actions.forgotPassError
          });
        } catch (e) {
          handleRecaptchaError(e, dispatch, actions.forgotPassError);
          setIsResending(false);
          return;
        }
        if (!recaptchaToken) {
          dispatch(actions.forgotPassError('reCAPTCHA verification failed. Please try again.'));
          setIsResending(false);
          return;
        }
      }

      const callback = (success) => {
        setIsResending(false);
        if (success) {
          setDialogMessage('We sent you another link. Please check your inbox.');
          setShowDialog(true);
        }
      };

      dispatch(actions.forgotPassword({ email: verifyEmail, recaptchaToken }, callback));
    } catch (err) {
      console.error('Forgot password error:', err);
      dispatch(actions.forgotPassError('An error occurred while resending the verification email. Please try again.'));
      setIsResending(false);
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setDialogMessage('');
    // Clear the error when dialog is closed
    if (errorset) {
      dispatch(actions.forgotPassError(''));
    }
  };

  return (
    <>
      <AuthWrapper>
        <div className="login-form-title">Check your email</div>
        <div className={styles.verificationContent}>
          {/* Add reCAPTCHA component */}
          {config.recaptcha.ENABLE_GOOGLE_RECAPTCHA && (
            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={config.recaptcha.SITE_KEY}
              onErrored={onRecaptchaErrored}
              onExpired={onRecaptchaExpired}
            />
          )}

          <div>We sent an email to</div>
          <div className={styles.verifyEmail}>{verifyEmail}</div>
          <br />
          <div>Not seeing the email?</div>
          <div>
            <button
              onClick={handleResend}
              className={`btn ${styles.resendBtn}`}
              disabled={isResending || isExecutingCaptcha}
            >
              {isResending || isExecutingCaptcha ? 'Sending...' : 'Resend'}
            </button>
          </div>
          <div>
            Still not seeing the email? Your account may be associated with a
            different email.
          </div>
          <div className={styles.contactSupport}>
            If you need help{' '}
            <span>
              <a href="mailto:support@datarova.com">contact support</a>
            </span>
          </div>
          <div style={{ paddingTop: '40px' }} className="kt-login__back">
            <Link to="/auth">
              <ArrowBackIcon />
            </Link>
            <span>Back to Sign in</span>
          </div>
        </div>
        {showDialog && (
          <DialogNotifyWindow
            text={dialogMessage}
            open={showDialog}
            handleClose={handleCloseDialog}
          />
        )}
      </AuthWrapper>
    </>
  );
};

export default SendVerification;
